import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import {
  AlertTriangle,
  Ban,
  Copy,
  FileText,
  MoreHorizontal,
  Phone,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  type CompanyStatus,
  formatDateTime,
  formatDateTimeWithRelative,
  formatNumber,
  messages,
  telHref,
  type CompanyDetail,
  type TimelineEntry,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SkeletonRows } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfidenceMark, EvidencePopover } from '@/components/common/evidence';
import { EmptyState } from '@/components/common/feedback';
import { BottomActions, PageHeader } from '@/components/common/page';
import { StatusChangeDialog } from '@/features/companies/status-dialog';
import { ContactDialog } from '@/features/companies/contact-dialog';
import { MergeDialog } from '@/features/companies/merge-dialog';
import { ActivityDialog } from '@/features/companies/activity-dialog';
import { SecurityPanel } from '@/features/companies/security-panel';
import { cn, tabTriggerClass } from '@/lib/utils';

const TABS = [
  { value: 'overview', label: messages.company.tabOverview },
  { value: 'contacts', label: messages.company.tabContacts },
  { value: 'timeline', label: messages.company.tabTimeline },
  { value: 'messages', label: messages.company.tabMessages },
  { value: 'security', label: messages.company.tabSecurity },
  { value: 'notes', label: messages.company.tabNotes },
];

/** S31 企業詳細 */
export const CompanyDetailPage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'overview';

  const [statusOpen, setStatusOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: qk.company(id),
    queryFn: () => api.get<SingleResponse<CompanyDetail>>(`/companies/${id}`),
  });

  const { data: duplicates } = useQuery({
    queryKey: qk.companyDuplicates(id),
    queryFn: () =>
      api.get<SingleResponse<{ company: { id: string; name: string }; reason: string }[]>>(
        `/companies/${id}/duplicates`,
      ),
    enabled: !!data,
  });

  const { data: timeline } = useQuery({
    queryKey: qk.companyTimeline(id),
    queryFn: () => api.get<SingleResponse<TimelineEntry[]>>(`/companies/${id}/timeline`),
    enabled: tab === 'timeline' || tab === 'notes',
  });

  const recrawl = useMutation({
    mutationFn: () => api.post(`/companies/${id}/recrawl`),
    onSuccess: () => toast.success('再取得を開始しました', { description: '完了すると内容が更新されます' }),
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (isLoading) return <SkeletonRows rows={8} />;
  if (error || !data) {
    return (
      <EmptyState
        title={messages.errors.notFound}
        action={{ label: '企業一覧へ', onClick: () => navigate('/companies') }}
      />
    );
  }

  const company = data.data;
  const duplicateCount = duplicates?.data.length ?? 0;
  const primaryPhone = company.contacts.find((c) => c.type === 'phone' && c.isValid);

  const setTab = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });
  };

  return (
    <>
      <PageHeader
        back="/companies"
        title={company.name}
        badge={<StatusBadge kind="company_status" value={company.status} size="lg" />}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{company.industry ?? '業種未設定'}</span>
            <ConfidenceMark level={company.industryConfidence} />
            <span aria-hidden>・</span>
            <span>{[company.prefecture, company.city].filter(Boolean).join('') || '地域未設定'}</span>
            <span aria-hidden>・</span>
            <span>担当 {company.assignee?.name ?? messages.common.unassigned}</span>
          </span>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setStatusOpen(true)}>
              {messages.company.changeStatus}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="その他の操作"
              aria-expanded={menuOpen}
            >
              <MoreHorizontal aria-hidden />
            </Button>
          </>
        }
      />

      {menuOpen && (
        <Card className="mb-3">
          <CardContent className="flex flex-wrap gap-2 p-3">
            <Button variant="outline" size="sm" onClick={() => recrawl.mutate()} disabled={recrawl.isPending}>
              <RefreshCw aria-hidden className="size-4" />
              {messages.company.recrawl}
            </Button>
            {duplicateCount > 0 && hasRole('manager') && (
              <Button variant="outline" size="sm" onClick={() => setMergeOpen(true)}>
                <Copy aria-hidden className="size-4" />
                {messages.company.merge}
              </Button>
            )}
            {!company.isExcluded && (
              <Button variant="outline" size="sm" onClick={() => setStatusOpen(true)}>
                <Ban aria-hidden className="size-4" />
                {messages.company.exclude}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setActivityOpen(true)}>
              <FileText aria-hidden className="size-4" />
              メモを追加
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 重複候補の案内（ヘッダ下） */}
      {duplicateCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border-2 border-foreground bg-muted p-3">
          <AlertTriangle aria-hidden className="size-5 shrink-0" />
          <p className="min-w-0 flex-1 text-body font-semibold">
            {messages.company.duplicateNotice(duplicateCount)}
          </p>
          <Button variant="outline" size="sm" onClick={() => setMergeOpen(true)}>
            {messages.company.duplicateCheck}
          </Button>
        </div>
      )}

      {company.isExcluded && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border-2 border-foreground bg-muted p-3">
          <Ban aria-hidden className="size-5 shrink-0" />
          <p className="min-w-0 flex-1 text-body font-semibold">
            この企業は対象外です{company.excludedReason ? `：${company.excludedReason}` : ''}
          </p>
          {hasRole('manager') && (
            <UnexcludeButton companyId={id} onDone={() => queryClient.invalidateQueries({ queryKey: qk.company(id) })} />
          )}
        </div>
      )}

      {company.tags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {company.tags.map((tag) => (
            <Badge key={tag.id}>{tag.name}</Badge>
          ))}
        </div>
      )}

      <Tabs.Root value={tab} onValueChange={setTab}>
        <Tabs.List className="scroll-x mb-4 flex gap-1 border-b border-border" aria-label="企業の情報">
          {TABS.map((item) => (
            <Tabs.Trigger
              key={item.value}
              value={item.value}
              className={tabTriggerClass}
            >
              {item.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{messages.company.tabOverview}</CardTitle>
              <span className="text-note text-foreground-muted">
                {messages.company.lastCrawled}{' '}
                <span className="tabular">{formatDateTime(company.lastCrawledAt)}</span>
              </span>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border">
                <OverviewRow label="会社名" sources={company.fieldSources.name}>
                  {company.name}
                </OverviewRow>
                <OverviewRow label="URL">
                  {company.websiteUrl ? (
                    <a
                      href={company.websiteUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="break-all underline underline-offset-2"
                    >
                      {company.websiteUrl}
                    </a>
                  ) : (
                    '—'
                  )}
                </OverviewRow>
                <OverviewRow label="住所" sources={company.fieldSources.addressLine}>
                  {[company.postalCode && `〒${company.postalCode}`, company.prefecture, company.city, company.addressLine]
                    .filter(Boolean)
                    .join(' ') || '—'}
                </OverviewRow>
                <OverviewRow label="電話" sources={company.fieldSources.phone}>
                  {primaryPhone ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="tabular">{primaryPhone.value}</span>
                      {/* 電話番号をタップすると端末の発信画面を開く */}
                      <Button variant="outline" size="sm" asChild>
                        <a href={telHref(primaryPhone.value)}>
                          <Phone aria-hidden className="size-4" />
                          {messages.company.call}
                        </a>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          void navigator.clipboard.writeText(primaryPhone.value);
                          toast.success(messages.common.copied);
                        }}
                      >
                        {messages.common.copy}
                      </Button>
                    </span>
                  ) : (
                    '—'
                  )}
                </OverviewRow>
                <OverviewRow label="業種" sources={company.fieldSources.industry}>
                  <span className="flex items-center gap-1.5">
                    {company.industry ?? '—'}
                    <ConfidenceMark level={company.industryConfidence} />
                  </span>
                </OverviewRow>
                <OverviewRow label="従業員">{company.employeeSize ?? '—'}</OverviewRow>
                <OverviewRow label="代表者" sources={company.fieldSources.representative}>
                  {company.representative ?? '—'}
                </OverviewRow>
                <OverviewRow label="資本金">
                  {company.capital != null ? `${formatNumber(company.capital)} 円` : '—'}
                </OverviewRow>
                <OverviewRow label="設立">{company.foundedOn ?? '—'}</OverviewRow>
                <OverviewRow label="事業内容" sources={company.fieldSources.description}>
                  <span className="space-y-1">
                    <span className="block whitespace-pre-wrap">{company.description ?? '—'}</span>
                    {company.description && company.descriptionConfidence !== 'high' && (
                      <span className="inline-flex items-center gap-1 text-note text-foreground-muted">
                        <Sparkles aria-hidden className="size-3.5" />
                        {messages.company.aiSummary}
                        <ConfidenceMark level={company.descriptionConfidence} />
                      </span>
                    )}
                  </span>
                </OverviewRow>
              </dl>
            </CardContent>
          </Card>
        </Tabs.Content>

        <Tabs.Content value="contacts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{messages.company.tabContacts}</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setContactOpen(true)}>
                連絡先を追加する
              </Button>
            </CardHeader>
            <CardContent>
              {company.contacts.length === 0 ? (
                <p className="text-body text-foreground-muted">{messages.company.noContactWarning}</p>
              ) : (
                <ul className="divide-y divide-border">
                  {company.contacts.map((contact) => (
                    <li key={contact.id} className="flex flex-wrap items-center gap-2 py-3">
                      <StatusBadge kind="contact_type" value={contact.type} />
                      <span className={cn('min-w-0 flex-1 break-all text-body', !contact.isValid && 'line-through opacity-60')}>
                        {contact.value}
                      </span>
                      {contact.isPrimary && <Badge variant="solid">主</Badge>}
                      {!contact.isValid && (
                        <Badge variant="dashed">無効{contact.invalidReason ? `・${contact.invalidReason}` : ''}</Badge>
                      )}
                      <ConfidenceMark level={contact.confidence} />
                      {contact.sources && contact.sources.length > 0 && (
                        <EvidencePopover sources={contact.sources} />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {company.stores.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>店舗・拠点（{company.stores.length}）</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border">
                  {company.stores.map((store) => (
                    <li key={store.id} className="py-2.5">
                      <p className="text-body font-semibold">{store.name}</p>
                      <p className="text-note text-foreground-muted">
                        {[store.prefecture, store.city, store.addressLine].filter(Boolean).join('') || '—'}
                        {store.phone && <span className="tabular"> ・{store.phone}</span>}
                      </p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </Tabs.Content>

        <Tabs.Content value="timeline">
          <Card>
            <CardContent className="pt-4 lg:pt-5">
              {!timeline || timeline.data.length === 0 ? (
                <p className="text-body text-foreground-muted">履歴はまだありません</p>
              ) : (
                <ul className="space-y-3">
                  {timeline.data.map((entry) => (
                    <li key={`${entry.type}-${entry.id}`} className="flex gap-3">
                      <span className="tabular w-28 shrink-0 text-note text-foreground-muted">
                        {formatDateTime(entry.occurredAt)}
                      </span>
                      <div className="min-w-0 flex-1 space-y-1 border-l border-border pl-3">
                        <p className="flex flex-wrap items-center gap-2 text-body">
                          <span>{entry.title}</span>
                          {entry.badgeKind && entry.badgeValue && (
                            <StatusBadge
                              kind={entry.badgeKind as never}
                              value={entry.badgeValue}
                            />
                          )}
                        </p>
                        {entry.summary && (
                          <p className="text-note text-foreground-muted">{entry.summary}</p>
                        )}
                        {entry.details && entry.details.length > 0 && (
                          <ul className="space-y-0.5 text-note text-foreground-muted">
                            {entry.details.map((line) => (
                              <li key={line} className="break-all">
                                ・{line}
                              </li>
                            ))}
                          </ul>
                        )}
                        {entry.href && (
                          <Button variant="link" size="sm" onClick={() => navigate(entry.href!)}>
                            {messages.common.detail}
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </Tabs.Content>

        <Tabs.Content value="messages">
          <CompanyMessages companyId={id} />
        </Tabs.Content>

        <Tabs.Content value="security">
          <SecurityPanel companyId={id} websiteUrl={company.websiteUrl} />
        </Tabs.Content>

        <Tabs.Content value="notes">
          <Card>
            <CardHeader>
              <CardTitle>{messages.company.tabNotes}</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setActivityOpen(true)}>
                {messages.activity.create}
              </Button>
            </CardHeader>
            <CardContent>
              {!timeline || timeline.data.filter((e) => e.type === 'activity').length === 0 ? (
                <p className="text-body text-foreground-muted">メモはまだありません</p>
              ) : (
                <ul className="divide-y divide-border">
                  {timeline.data
                    .filter((e) => e.type === 'activity')
                    .map((entry) => (
                      <li key={entry.id} className="space-y-1 py-3">
                        <p className="text-body font-semibold">{entry.title}</p>
                        {entry.summary && (
                          <p className="whitespace-pre-wrap text-body text-foreground-muted">{entry.summary}</p>
                        )}
                        <p className="text-note text-foreground-subtle">
                          {formatDateTimeWithRelative(entry.occurredAt)}
                          {entry.actor?.name ? `・${entry.actor.name}` : ''}
                        </p>
                      </li>
                    ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </Tabs.Content>
      </Tabs.Root>

      {/* スマホ：主要操作を下部固定 */}
      <BottomActions className="lg:hidden">
        {primaryPhone && (
          <Button variant="outline" size="lg" className="flex-1" asChild>
            <a href={telHref(primaryPhone.value)}>
              <Phone aria-hidden />
              発信
            </a>
          </Button>
        )}
        <Button variant="outline" size="lg" className="flex-1" onClick={() => setTab('messages')}>
          文面
        </Button>
        <Button size="lg" className="flex-1" onClick={() => setStatusOpen(true)}>
          状況を変更
        </Button>
      </BottomActions>

      <StatusChangeDialog
        open={statusOpen}
        onOpenChange={setStatusOpen}
        companyId={id}
        currentStatus={company.status as CompanyStatus}
      />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} companyId={id} />
      <MergeDialog open={mergeOpen} onOpenChange={setMergeOpen} company={company} />
      <ActivityDialog open={activityOpen} onOpenChange={setActivityOpen} companyId={id} />
    </>
  );
};

const OverviewRow = ({
  label,
  sources,
  children,
}: {
  label: string;
  sources?: unknown[];
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1 py-2.5 lg:flex-row lg:items-baseline lg:gap-4">
    <dt className="w-28 shrink-0 text-note text-foreground-muted">{label}</dt>
    <dd className="min-w-0 flex-1 text-body text-foreground">{children}</dd>
    {sources && sources.length > 0 && (
      <EvidencePopover sources={sources as never} className="shrink-0" />
    )}
  </div>
);

const UnexcludeButton = ({ companyId, onDone }: { companyId: string; onDone: () => void }) => {
  const mutation = useMutation({
    mutationFn: () => api.del(`/companies/${companyId}/exclude`),
    onSuccess: () => {
      toast.success('対象外を解除しました');
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Button variant="outline" size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
      {messages.company.unexclude}
    </Button>
  );
};

const CompanyMessages = ({ companyId }: { companyId: string }) => {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: qk.messages({ companyId }),
    queryFn: () =>
      api.get<{ data: { id: string; subject: string | null; status: string; channel: string; purpose: string; version: number; createdAt: string }[] }>(
        '/messages',
        { companyId, limit: 50 },
      ),
  });

  if (!data || data.data.length === 0) {
    return (
      <Card>
        <CardContent className="pt-4 lg:pt-5">
          <p className="text-body text-foreground-muted">
            この企業向けの文面はまだありません。キャンペーンから生成できます
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4 lg:pt-5">
        <ul className="divide-y divide-border">
          {data.data.map((message) => (
            <li key={message.id}>
              <button
                type="button"
                onClick={() => navigate(`/messages/${message.id}`)}
                className="flex w-full flex-wrap items-center gap-2 py-3 text-left hover:bg-muted"
              >
                <span className="min-w-0 flex-1 truncate text-body">{message.subject ?? '（件名なし）'}</span>
                <StatusBadge kind="message_status" value={message.status} />
                <StatusBadge kind="channel" value={message.channel} />
                <span className="tabular text-note text-foreground-subtle">v{message.version}</span>
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
